import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useSearchParams } from 'react-router-dom';

import { can } from '@/config/access';
import {
  BRAND_HUB_ROUTES,
  CAMPAIGN_SECTION_ROUTES,
  UNLISTED_ROUTES,
  WORKSPACE_ROUTES,
} from '@/config/routes';
import type { PageHeading } from '@/components/layout/AppShell';
import { useActiveMembership, useSession } from '@/features/auth/session';
import { isBrandTab } from '@/features/brandHub/brand/brandTabs';
import { BRAND_CREATE_STEPS, readBrandCreateStep } from '@/features/brandHub/brandCreate/brandCreateSteps';
import { TEMPLATES_PATH } from '@/features/brandHub/paths';
import { useBrands } from '@/features/brandHub/brand/useBrands';
import { useTemplates } from '@/features/brandHub/templates/useTemplates';
import { useCampaign, useCampaigns } from '@/features/campaigns/useCampaigns';
import { WIZARD_STEP_KEYS, readStep } from '@/features/campaigns/wizardSteps';

export function usePageHeading(): PageHeading {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const campaignMatch = matchPath('/campaigns/:campaignId/*', pathname);
  const campaignId = campaignMatch?.params.campaignId;
  const { campaign } = useCampaign(campaignId);
  const { data: campaigns } = useCampaigns();
  const session = useSession();
  const membership = useActiveMembership();
  const brandMatch = matchPath('/brand-hub/brand/:brandSlug/*', pathname);
  const brandSlug = brandMatch?.params.brandSlug;
  const { data: brands } = useBrands();
  const { data: templates } = useTemplates();

  const campaignsCrumb = { label: t('campaigns.allCampaigns'), to: '/campaigns' };

  if (matchPath('/campaigns/new', pathname)) {
    const step = readStep(searchParams);
    return {
      title: t('campaignForm.createTitle'),
      subtitle: t('campaignForm.stepProgress', {
        step,
        total: WIZARD_STEP_KEYS.length,
        name: t(WIZARD_STEP_KEYS[step - 1]),
      }),
      crumb: { label: t('nav.campaigns'), to: '/campaigns' },
    };
  }

  if (campaignId && matchPath('/campaigns/:campaignId/edit', pathname)) {
    return {
      title: t('campaignForm.editTitle'),
      subtitle: campaign ? `${campaign.name} · ${campaign.brand}` : undefined,
      crumb: {
        label: t('nav.section.overview'),
        to: `/campaigns/${campaignId}/overview`,
      },
    };
  }

  if (campaignId) {
    const section = CAMPAIGN_SECTION_ROUTES.find((route) =>
      matchPath(`/campaigns/:campaignId/${route.path}`, pathname),
    );
    if (section) {
      const isOverview = section.path === 'overview';
      return {
        title: isOverview && campaign ? campaign.name : t(section.titleKey),
        subtitle: campaign
          ? isOverview
            ? `${campaign.brand} · ${campaign.type}`
            : `${campaign.name} · ${campaign.brand}`
          : undefined,
        crumb: campaignsCrumb,
      };
    }
  }

  if (matchPath({ path: '/campaigns', end: true }, pathname)) {
    const active = campaigns.filter((item) => item.status === 'active').length;
    return {
      title: t('campaigns.title'),
      subtitle: t('campaigns.countSummary', { count: campaigns.length, active }),
    };
  }

  if (matchPath({ path: '/dashboard', end: true }, pathname)) {
    return {
      title: t('dashboard.title'),
      subtitle: t('dashboard.subtitle', {
        date: new Date().toLocaleDateString(i18n.language, {
          weekday: 'long',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }),
      }),
    };
  }

  if (matchPath('/brand-hub/brand/new', pathname)) {
    const step = readBrandCreateStep(searchParams);
    return {
      title: t('brandHub.brandCreate.title'),
      subtitle: t('brandHub.brandCreate.progress', {
        step: BRAND_CREATE_STEPS.indexOf(step) + 1,
        total: BRAND_CREATE_STEPS.length,
        name: t(`brandHub.brandCreate.step.${step}`),
      }),
      crumb: { label: t('nav.brands'), to: '/brand-hub' },
    };
  }

  if (brandSlug && brandSlug !== 'new') {
    const brand = brands.find((one) => one.id === brandSlug);
    const productRoute = UNLISTED_ROUTES.find(
      (route) => route.path.includes('/products/') && matchPath(route.path, pathname),
    );
    if (productRoute) {
      return {
        title: t(productRoute.titleKey),
        subtitle: brand?.name,
        crumb: { label: brand?.name ?? t('nav.brands'), to: `/brand-hub/brand/${brandSlug}/products` },
      };
    }
    const tab = matchPath('/brand-hub/brand/:brandSlug/:tab', pathname)?.params.tab;
    const isOverview = !isBrandTab(tab) || tab === 'overview';
    const state = brand ? t(`brandHub.brand.state.${brand.state}`) : '';
    return {
      title: isOverview ? (brand?.name ?? t('brandHub.brand.title')) : t(`brandHub.brand.tabs.${tab}`),
      subtitle: brand ? (isOverview ? state : `${brand.name} · ${state}`) : undefined,
      crumb: { label: t('nav.brands'), to: '/brand-hub' },
    };
  }

  if (matchPath('/brand-hub/templates/new', pathname)) {
    return {
      title: t('brandHub.template.createTitle'),
      subtitle: t('brandHub.template.addHint'),
      crumb: { label: t('nav.templates'), to: TEMPLATES_PATH },
    };
  }

  const templateId = matchPath('/brand-hub/templates/:templateId', pathname)?.params.templateId;
  if (templateId) {
    const template = templates.find((one) => one.id === templateId);
    return {
      title: template?.name ?? t('brandHub.template.editTitle'),
      subtitle: template ? t('brandHub.template.editTitle') : undefined,
      crumb: { label: t('nav.templates'), to: TEMPLATES_PATH },
    };
  }

  if (matchPath({ path: '/settings', end: true }, pathname)) {
    return {
      title: t('settings.title'),
      subtitle: session
        ? `${session.name} · ${t(`settings.role.${membership?.role ?? 'member'}`)}`
        : undefined,
    };
  }

  const staticRoute = [...UNLISTED_ROUTES, ...BRAND_HUB_ROUTES, ...WORKSPACE_ROUTES].find(
    (route) =>
      matchPath({ path: route.path, end: route.end }, pathname) && can(membership, route.action),
  );
  if (staticRoute) {
    return {
      title: t(staticRoute.titleKey),
      subtitle: staticRoute.subtitleKey ? t(staticRoute.subtitleKey) : undefined,
    };
  }

  return { title: t('notFound.title') };
}
